import React from 'react';
import { Box, Typography, Grid, styled } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import CustomModal from '../../Common/CustomModal';
import LabeledInput from '../../Common/LabeledInput';
import StyledBackButton from '../../Common/StyledBackButton';
import StyledButton from '../../Common/StyledButton';
import { useAppSelector } from '../../../redux/hooks';
import { RootState } from '../../../redux/rootReducer';
import { createNewFolder, renameFolder } from '../../../redux/activityLibrary/activityLibrarySlice';

const StyledGrid = styled(Grid)(() => ({
  maxHeight: 167,
  background: `linear-gradient(0deg, rgba(124, 174, 190, 0.2), rgba(124, 174, 190, 0.2))`,
  gap: '11px',
}));

interface CreateRenameFolderModalProps {
  isOpen: boolean;
  isCreateFolder: boolean;
  initialFolderName: string;
  id?: string;
  handleDismiss: () => void;
}

const CreateRenameFolderModal = ({
  isOpen,
  isCreateFolder,
  initialFolderName,
  id,
  handleDismiss,
}: CreateRenameFolderModalProps) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const { createNewFolderLoading, renameFolderLoading } = useAppSelector(
    (state: RootState) => state.activityLibrary
  );

  const coachId = useAppSelector((state: RootState) => state.coach.coach?.id);

  const formik = useFormik({
    initialValues: {
      folderName: initialFolderName,
    },
    validationSchema: Yup.object().shape({
      folderName: Yup.string()
        .matches(/^[^<>:"/\\|?*]+$/, {
          message: t('activityLibrary.createRenameFolderModal.validations.folderNameInvalid'),
        })
        .required(t('activityLibrary.createRenameFolderModal.validations.folderNameRequired'))
        .notOneOf(
          [
            t('activityLibrary.createRenameFolderModal.validations.trash'),
            t('activityLibrary.createRenameFolderModal.validations.Trash'),
          ],
          t('activityLibrary.createRenameFolderModal.validations.canNotBeTrash')
        ),
    }),
    onSubmit: (v) => {
      if (coachId) {
        const payload = { name: v.folderName, coachId };
        if (isCreateFolder) {
          dispatch(createNewFolder(payload));
        } else if (id) {
          dispatch(renameFolder({ ...payload, id }));
        }
      }
    },
  });

  const handleDismissOnClick = () => {
    formik.resetForm();
    handleDismiss();
  };

  return (
    <CustomModal isOpen={isOpen} width={391}>
      <Box width={391} minHeight={427}>
        <StyledGrid container py={4.5}>
          <Grid item xs={12} textAlign="center">
            {isCreateFolder ? (
              <Box component="img" src="assets/images/folder.png" />
            ) : (
              <Box component="img" src="assets/images/pen.png" />
            )}
          </Grid>
          <Grid item xs={12} textAlign="center">
            <Typography variant="title2Bold" color="#6599A5">
              {isCreateFolder
                ? t('activityLibrary.createRenameFolderModal.createTitle')
                : t('activityLibrary.createRenameFolderModal.renameTitle')}
            </Typography>
          </Grid>
        </StyledGrid>
        <form onSubmit={formik.handleSubmit}>
          <Grid container minHeight={260} gap={6} px={4} pt={6.5} pb={4.5}>
            <Grid item xs={12}>
              <LabeledInput
                label={t('activityLibrary.createRenameFolderModal.folderName')}
                id="folder-name"
                type="text"
                name="folderName"
                value={formik.values.folderName}
                handleChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.errors.folderName ?? undefined}
                placeholder={t('activityLibrary.createRenameFolderModal.folderNamePlaceholder')}
              />
            </Grid>
            <Grid item xs={12} display="flex" gap="20px">
              <StyledBackButton
                disabled={createNewFolderLoading || renameFolderLoading}
                onClick={handleDismissOnClick}
                fullWidth
              >
                {t('common.dismiss')}
              </StyledBackButton>
              <StyledButton
                loading={createNewFolderLoading || renameFolderLoading}
                loadingText={
                  isCreateFolder
                    ? t('common.creating')
                    : t('activityLibrary.createRenameFolderModal.renaming')
                }
                fullWidth
              >
                {isCreateFolder
                  ? t('common.create')
                  : t('activityLibrary.createRenameFolderModal.rename')}
              </StyledButton>
            </Grid>
          </Grid>
        </form>
      </Box>
    </CustomModal>
  );
};

export default CreateRenameFolderModal;
